// ScenarioEditor.js
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Trash } from 'lucide-react';
import { useScenarioEditor } from '../hooks/useScenarioEditor';

// Import component sections
import BasicInfoSection from './sections/BasicInfoSection';
import ValidateLogicSection from './sections/ValidateLogicSection';
import CreateLogicSection from './sections/CreateLogicSection';
import DisplayLogicSection from './sections/DisplayLogicSection';
import OfflineLogicSection from './sections/OfflineLogicSection';

const ScenarioEditor = ({ supabase }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const {
    isEditing,
    formData,
    isLoading,
    isSaving,
    isLoadingBases,
    isLoadingTables,
    isLoadingFields,
    error,
    validationErrors,
    airtableBases,
    airtableTables,
    airtableFields,
    // Actions
    updateFormInput,
    toggleSection,
    updateValidateBase,
    updateValidateTable,
    updateValidateField,
    addValidationCondition,
    removeValidationCondition,
    updateValidationCondition,
    addValidationGroup,
    updateValidationGroup,
    removeValidationGroup,
    moveConditionToGroup,
    updateValidationMainOperator,
    updateCreateBase,
    updateCreateTable,
    addCreateField,
    removeCreateField,
    updateOverrideSettings,
    addDisplayField,
    removeDisplayField,
    updateOfflineLastModifiedField,
    updateOfflineRecordsList,
    saveScenario,
    deleteScenario
  } = useScenarioEditor(supabase, id);

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const success = await saveScenario();
    if (success) {
      navigate('/scenarios');
    }
  };

  // Delete scenario handler
  const handleDelete = async () => {
    if (!isEditing) return;
    
    const confirmDelete = window.confirm('Are you sure you want to delete this scenario? This action cannot be undone.');
    
    if (!confirmDelete) return;
    
    const success = await deleteScenario();
    if (success) {
      navigate('/scenarios');
    }
  };

  // Cancel handler
  const handleCancel = () => {
    navigate('/scenarios');
  };

  if (isLoading && !isSaving) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading scenario...</div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg max-w-4xl mx-auto p-6">
      <div className="flex items-center mb-6">
        <button 
          onClick={handleCancel}
          className="mr-3 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h2 className="text-xl font-semibold">
          {isEditing ? 'Edit Scenario' : 'Create New Scenario'}
        </h2>
      </div>
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Information */}
          <BasicInfoSection 
            formData={formData}
            isEditing={isEditing}
            validationErrors={validationErrors}
            handleInputChange={updateFormInput}
          />
          
          {/* Validation Logic */}
          <ValidateLogicSection 
            formData={formData}
            validationErrors={validationErrors}
            airtableBases={airtableBases}
            airtableTables={airtableTables.validate}
            airtableFields={airtableFields.validate}
            isLoadingBases={isLoadingBases}
            isLoadingTables={isLoadingTables.validate}
            isLoadingFields={isLoadingFields.validate}
            toggleSection={() => toggleSection('hasValidateLogic')}
            updateValidateBase={updateValidateBase}
            updateValidateTable={updateValidateTable}
            updateValidateField={updateValidateField}
            addValidationCondition={addValidationCondition}
            removeValidationCondition={removeValidationCondition}
            updateValidationCondition={updateValidationCondition}
            addValidationGroup={addValidationGroup}
            updateValidationGroup={updateValidationGroup}
            removeValidationGroup={removeValidationGroup}
            moveConditionToGroup={moveConditionToGroup}
            updateValidationMainOperator={updateValidationMainOperator}
          />
          
          {/* Create Logic */}
          <CreateLogicSection
            formData={formData}
            validationErrors={validationErrors}
            airtableBases={airtableBases}
            airtableTables={airtableTables.create}
            airtableFields={airtableFields.create}
            isLoadingBases={isLoadingBases}
            isLoadingTables={isLoadingTables.create}
            isLoadingFields={isLoadingFields.create}
            toggleSection={() => toggleSection('hasCreateLogic')}
            updateCreateBase={updateCreateBase}
            updateCreateTable={updateCreateTable}
            addCreateField={addCreateField}
            removeCreateField={removeCreateField}
            updateOverrideSettings={updateOverrideSettings}
          />
          
          {/* Display Logic */}
          <DisplayLogicSection 
            formData={formData}
            validationErrors={validationErrors}
            airtableFields={airtableFields}
            toggleSection={() => toggleSection('hasDisplayLogic')}
            addDisplayField={addDisplayField}
            removeDisplayField={removeDisplayField}
          />
          
          {/* Offline Logic */}
          <OfflineLogicSection 
            formData={formData}
            validationErrors={validationErrors}
            airtableFields={airtableFields}
            isLoadingFields={isLoadingFields.validate}
            toggleSection={() => toggleSection('hasOfflineLogic')}
            updateOfflineLastModifiedField={updateOfflineLastModifiedField}
            updateOfflineRecordsList={updateOfflineRecordsList}
          />
          
          {/* Save Button */}
          <div className="pt-4 flex justify-end">
            <button
              type="button"
              onClick={handleCancel}
              className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              disabled={isSaving}
            >
              Cancel
            </button>
            {isEditing && (
              <button
                type="button"
                onClick={handleDelete}
                className="mr-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                disabled={isSaving}
              >
                <Trash className="h-4 w-4 mr-2" />
                Delete
              </button>
            )}
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Scenario' : 'Create Scenario'}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ScenarioEditor;